import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatMenu, MatMenuTrigger } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { MatFieldComponent } from '../../../../common/components';
import { BaseComponent } from '../../../../common/components/base.component';
import { EditorChoiceDataSource, HubEvent } from '../../../../models/hub/hub.event';
import { HubPlace } from '../../../../models/hub/hub.place';
import { HubTour } from '../../../../models/hub/hub.tour';
import { HubVisitorFeatureToggle } from '../../../../models/hub/hub.visitor.feature.toggle';
import { EventTimeService } from '../../../../services/event.time.service';
import { ImageService } from '../../../../services/image.service';
import {
  AllFrontendSettingsStore,
  VisitorTripNotifyAddOrRemoveItemStore,
} from '../../../../stores/visitor.calendar.store';
import { VisitorTripSideBarComponent } from '../trip.side.bar/visitor.trip.side.bar.component';
import { VisitorTripComponent } from '../visitor.trip.component';
import { AllFrontendSettingsCommand } from '../../../../commands/visitor/all.frontend.settings.command';
import { isNullOrUndefined } from '../../../../common/extensions';
import { StyleModel } from '../../../../commands/hub/style.command';
import { EditorChoiceStore } from '../../../../stores/hub.calendar.store';

@Component({
  selector: 'ig-visitor-trip-card',
  templateUrl: './visitor.trip.card.component.html',
  styleUrls: ['./visitor.trip.card.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class VisitorTripCardComponent extends BaseComponent implements OnInit {
  @Input() public item: HubPlace | HubEvent | HubTour;
  @Input() public itemType: string;
  @Input() public featuresToggle = new HubVisitorFeatureToggle();
  @Input() public titleBgColor = '#49659f';
  public tripFormGroup: FormGroup;
  public newTripTitle: string;
  public isAddingNewTrip = false;
  @ViewChild('menuTrigger') public menuTrigger: MatMenuTrigger;
  @ViewChild('tripTitle') public tripTitle: MatFieldComponent;
  @Output() addTo = new EventEmitter<any>();
  @Output() removeFrom = new EventEmitter<any>();
  @Output() locateOnMap = new EventEmitter<any>();
  @Output() showDetails = new EventEmitter<any>();
  public hubName: string;
  public detailDate: string;
  public detailTime: string;
  public totalTime: string;
  public totalStop: number;
  public isMultiDateEvent = false;
  public isInAnyTrip = false;
  public isAddingSuccess = [];
  public inTripStatus = [];
  public style = new StyleModel();
  public editorChoiceText = "EDITOR'S CHOICE";

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    public imageService: ImageService,
    public parent: VisitorTripSideBarComponent,
    private allFrontendSettingsStore: AllFrontendSettingsStore,
    private notifyAddOrRemoveItemStore: VisitorTripNotifyAddOrRemoveItemStore,
    private visitorTripComponent: VisitorTripComponent,
    private editorChoiceStore: EditorChoiceStore,
    private activatedRoute: ActivatedRoute
  ) {
    super({ activatedRoute });
  }

  public ngOnInit() {
    let { hubName } = this.allRouteParams;
    this.hubName = hubName;

    this.tripFormGroup = this.formBuilder.group({
      tripTitle: [null, [Validators.required]],
    });

    if (this.itemType === 'event') {
      this.item = <HubEvent>this.item;
      if (this.item.eventTimes && this.item.eventTimes.length > 1) {
        this.isMultiDateEvent = true;
      }
      this.processEventTime(this.item);
    } else if (this.itemType === 'tour') {
      this.item = <HubTour>this.item;
      const attract = this.item as any;
      this.item.attractions = attract.attraction;
      this.totalStop = this.item.stops || this.item.attractions.filter((x) => x.status !== 'not found').length;
      this.totalTime = this.item.travelTime ? EventTimeService.convertToTravelTimeText(this.item.travelTime) : null;
    }

    this.notifyAddOrRemoveItemStore.notifyCommand.takeUntil(this.isDestroyed).subscribe((item) => {
      this.checkIsInAnyTrip();
    });

    this.allFrontendSettingsStore.allFrontendSettingsCommand
      .takeUntil(this.isDestroyed)
      .subscribe((command: AllFrontendSettingsCommand) => {
        if (command.style) {
          this.style = command.style as StyleModel;
        }
      });

    this.editorChoiceTextHandler();
  }

  public get isNoTripCreatedYet() {
    this.parent.loadTripsInLocalStorage();
    return !this.parent.trips || !this.parent.trips.length;
  }

  public editorChoiceTextHandler() {
    this.editorChoiceStore.infoCommand.takeUntil(this.isDestroyed).subscribe((editorChoice: EditorChoiceDataSource) => {
      if (!!this.item['startTime']) {
        this.editorChoiceText = editorChoice.editorChoiceForEvent;
      } else {
        this.editorChoiceText = editorChoice.editorChoiceForPlace;
      }
    });
  }

  public checkIsInAnyTrip() {
    this.parent.loadTripsInLocalStorage();
    if (!this.parent.trips || !this.parent.trips.length) {
      return false;
    }
    this.isInAnyTrip = false;
    this.parent.trips.forEach((trip) => {
      if (trip.attractions.any((x) => x.item.id === this.item.id)) {
        this.isInAnyTrip = true;
      }
    });
  }

  public openTripModal() {
    this.parent.showCreateTripModal(false, this.item, this.itemType, () => {
      this.showAddingToTripSuccess(0, 2000);
    });
  }

  public openTripMenu() {
    if (this.item['isOnlineEvent']) {
      alert('Online events cannot be added to your trips');
      return;
    }
    this.getInTripStatus();

    if (this.isNoTripCreatedYet) {
      this.openTripModal();
      this.menuTrigger.closeMenu();
      return;
    }

    this.parent.trips.forEach((x) => {
      this.isAddingSuccess.push(false);
    });
    this.menuTrigger.openMenu();
  }

  public getInTripStatus() {
    this.inTripStatus = [];

    let trips = this.parent.loadTripsInLocalStorage();
    if (!trips || !trips.trips || !trips.trips.length) {
      return null;
    }

    for (let trip of trips.trips) {
      let isInTrip = trip.attractions.any((x) => x.item.id === this.item.id);
      this.inTripStatus.push(isInTrip);
    }
  }

  public exitAddingNewTrip($event) {
    $event && $event.stopPropagation && $event.stopPropagation()();
    this.isAddingNewTrip = false;
  }

  public addNewTrip($event) {
    //$event.stopPropagation();
    this.isAddingNewTrip = true;
    setTimeout(() => {
      this.tripTitle.focus();
    });
  }

  public saveNewTrip() {
    this.parent.createNewTrip(this.newTripTitle, this.item, this.itemType);
    this.menuTrigger.closeMenu();
    this.newTripTitle = '';
    this.isAddingNewTrip = false;
  }

  public addOrRemoveFromTrip($event, tripIndex) {
    $event.stopPropagation();
    //if (this.isInTrip(tripIndex)) {
    if (this.inTripStatus[tripIndex]) {
      this.parent.removeFromTrip(this.item._id, tripIndex);
      this.inTripStatus[tripIndex] = false;
    } else {
      this.parent.addItemToTrip({ item: this.item, type: this.itemType, tripIndex });
      this.inTripStatus[tripIndex] = true;
    }
    this.showAddingToTripSuccess(tripIndex);
    this.checkIsInAnyTrip();
    this.notifyAddOrRemoveItemStore.notifyCommand.next(null);
  }

  public showAddingToTripSuccess(index, showTime = 1000) {
    this.isAddingSuccess = [];
    this.parent.trips.forEach((x) => {
      this.isAddingSuccess.push(false);
    });
    this.isAddingSuccess[index] = true;
    setTimeout(() => {
      this.isAddingSuccess[index] = false;
    }, showTime);
    setTimeout(() => {
      this.menuTrigger.closeMenu();
    }, 900);
  }

  public isInTrip(tripIndex) {
    return this.parent.isInTrip(tripIndex, this.item._id);
  }

  public newTripFieldFocus($event) {
    $event.stopPropagation();
  }

  public addItem($event) {
    if ($event && $event.stopPropagation) {
      $event.stopPropagation();
    }
    this.addTo.emit({ item: this.item, event: $event });
  }

  public removeItem($event) {
    if ($event && $event.stopPropagation) {
      $event.stopPropagation();
    }
    this.removeFrom.emit(this.item);
  }

  public showOnMap($event) {
    if ($event && $event.stopPropagation) {
      $event.stopPropagation();
    }
    this.locateOnMap.emit(this.item);
  }

  public viewDetails($event) {
    if ($event && $event.stopPropagation) {
      $event.stopPropagation();
    }
    this.showDetails.emit(this.item);
  }

  private processEventTime(event: HubEvent) {
    if (event.startTimeText) {
      if (!this.isMultiDateEvent) {
        this.detailDate = EventTimeService.getDateFromTimeText(event.startTimeText);
        this.detailTime = EventTimeService.getTimeFromTimeText(event.startTimeText, event.timezone);
      } else {
        const startDate = EventTimeService.getDateFromTimeText(event.startTimeText);
        const endDate = EventTimeService.getDateFromTimeText(event.endTimeText);

        this.detailDate = startDate + (endDate ? ' - ' + endDate : '');
      }
    } else {
      if (!this.isMultiDateEvent) {
        this.detailDate = EventTimeService.getDateFromTime(event.startTime);
        this.detailTime = EventTimeService.getTimeFromTime(event.startTime);
      } else {
        const startDate = EventTimeService.getDateFromTime(event.startTime);
        const endDate = EventTimeService.getDateFromTime(event.endTime);

        this.detailDate = startDate + (endDate ? ' - ' + endDate : '');
      }
    }
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/components/visitor/trip/trip.card/visitor.trip.card.component.ts